import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Download, TrendingDown } from 'lucide-react';

// Tab components for each spend view
const SpendByDivision = () => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Spend by Division</CardTitle>
      <Button variant="outline" size="sm">
        <Download className="h-4 w-4 mr-2" />
        Export to Excel
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Division</TableHead>
            <TableHead className="text-right">Total Spend</TableHead>
            <TableHead className="text-right">Orders</TableHead>
            <TableHead className="text-right">Avg Order Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-muted-foreground" colSpan={4}>
              No data available. Configure data source in Admin Settings.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const SpendByMonth = () => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Spend by Month</CardTitle>
      <Button variant="outline" size="sm">
        <Download className="h-4 w-4 mr-2" />
        Export to Excel
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            <TableHead className="text-right">Total Spend</TableHead>
            <TableHead className="text-right">Orders</TableHead>
            <TableHead className="text-right">YoY Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-muted-foreground" colSpan={4}>
              No data available. Configure data source in Admin Settings.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const SpendBySuppliers = () => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Spend by Suppliers</CardTitle>
      <Button variant="outline" size="sm">
        <Download className="h-4 w-4 mr-2" />
        Export to Excel
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Supplier</TableHead>
            <TableHead className="text-right">Total Spend</TableHead>
            <TableHead className="text-right">Orders</TableHead>
            <TableHead className="text-right">% of Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-muted-foreground" colSpan={4}>
              No data available. Configure data source in Admin Settings.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const SpendByCommodities = () => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Spend by Commodities</CardTitle>
      <Button variant="outline" size="sm">
        <Download className="h-4 w-4 mr-2" />
        Export to Excel
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Commodity</TableHead>
            <TableHead className="text-right">Total Spend</TableHead>
            <TableHead className="text-right">Suppliers</TableHead>
            <TableHead className="text-right">% of Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-muted-foreground" colSpan={4}>
              No data available. Configure data source in Admin Settings.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const SpendBySupplierThenProduct = () => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>By Supplier then by Product</CardTitle>
      <Button variant="outline" size="sm">
        <Download className="h-4 w-4 mr-2" />
        Export to Excel
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Supplier</TableHead>
            <TableHead>Product</TableHead>
            <TableHead className="text-right">Quantity</TableHead>
            <TableHead className="text-right">Spend</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-muted-foreground" colSpan={4}>
              No data available. Configure data source in Admin Settings.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const SpendLeakageTab = () => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="flex items-center gap-2">
        <TrendingDown className="h-5 w-5 text-red-500" />
        Spend Leakage
      </CardTitle>
      <Button variant="outline" size="sm">
        <Download className="h-4 w-4 mr-2" />
        Export to Excel
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead className="text-right">Leakage Amount</TableHead>
            <TableHead className="text-right">% of Category Spend</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-muted-foreground" colSpan={4}>
              No data available. Configure data source in Admin Settings.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

export default function SpendData() {
  const [activeTab, setActiveTab] = useState('division');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Spend Data</h1>
        <p className="text-muted-foreground">
          Analyze spending patterns across divisions, suppliers, and commodities
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="division">Spend by Division</TabsTrigger>
          <TabsTrigger value="month">Spend by month</TabsTrigger>
          <TabsTrigger value="suppliers">Spend by suppliers</TabsTrigger>
          <TabsTrigger value="commodities">Spend by commodities</TabsTrigger>
          <TabsTrigger value="supplier-product">By supplier then by product</TabsTrigger>
          <TabsTrigger value="leakage">Spend Leakage</TabsTrigger>
        </TabsList>

        <TabsContent value="division" className="mt-6">
          <SpendByDivision />
        </TabsContent>

        <TabsContent value="month" className="mt-6">
          <SpendByMonth />
        </TabsContent>

        <TabsContent value="suppliers" className="mt-6">
          <SpendBySuppliers />
        </TabsContent>

        <TabsContent value="commodities" className="mt-6">
          <SpendByCommodities />
        </TabsContent>

        <TabsContent value="supplier-product" className="mt-6">
          <SpendBySupplierThenProduct />
        </TabsContent>

        <TabsContent value="leakage" className="mt-6">
          <SpendLeakageTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
